import React, { useMemo, Fragment } from 'react';
import FormInput from 'src/components/form-input';
import FormSwitch from 'src/components/form-switch';
import IPInput from 'src/components/ip-input';
import { GlobalIpSubnetType } from 'src/context/global/types';
import { useGlobalContext } from 'src/context/global/use-global-context';
import {
  EDataType,
  IpInterface,
} from 'src/modules/network-configuration/models/node-data';
import { countLeading255Octets } from 'src/utils/ip';

import { INodeEditData } from './type';

interface IProps {
  id: string;
  values: INodeEditData;
  type: EDataType;
  setValues: (value: React.SetStateAction<INodeEditData>) => void;
  errors: {
    name: string;
    ip: string;
    subnet: string;
    subnetMask: string;
    gateway: string;
    ipInterfaces: {
      ip: string;
      subnet: string;
      subnetMask: string;
    }[];
  };
  ipInterfaces: IpInterface[];
  isManaged: boolean;
}

export default function EditGeneral({
  id,
  values,
  type,
  setValues,
  errors,
  ipInterfaces,
  isManaged,
}: IProps) {
  const { globalIpSubnet } = useGlobalContext();

  const hasVlan = useMemo(() => ipInterfaces.length > 0, [ipInterfaces]);

  return (
    <div
      className="mt-2 flex flex-col items-center justify-start gap-2"
      data-testid={`node-edit-general-panel-${id}`}
    >
      <FormInput
        id="node-name"
        name="nodeName"
        label="Name"
        data-testid={`node-edit-general-name-input-${id}`}
        value={values.name}
        wrapperClassName=""
        error={errors.name}
        onChange={(e) =>
          setValues((prev) => ({ ...prev, name: e.target.value }))
        }
      />
      {isManaged && (
        <div className="mt-4 flex w-full flex-col items-start justify-start gap-2">
          <table
            className="border-collapse border border-gray-300"
            data-testid={`node-edit-ip-table-${id}`}
          >
            <thead>
              <tr>
                {hasVlan && (
                  <th className="whitespace-nowrap border border-gray-300 px-2 text-center align-middle text-sm font-medium">
                    VLAN ID
                  </th>
                )}
                <th className="whitespace-nowrap border border-gray-300 px-2 text-center align-middle text-sm font-medium">
                  IP Address
                </th>
                <th className="whitespace-nowrap border border-gray-300 px-2 text-center align-middle text-sm font-medium">
                  Subnet Mask
                </th>
                {type === EDataType.Device && (
                  <th className="whitespace-nowrap border border-gray-300 px-2 text-center align-middle text-sm font-medium">
                    port-based DHCP
                  </th>
                )}
              </tr>
            </thead>
            {hasVlan ? (
              <tbody>
                {ipInterfaces.map((ipInterface, index) => {
                  const disabledLength = ipInterface.subnetMask
                    ? countLeading255Octets(ipInterface.subnetMask)
                    : -1;

                  const inputValue =
                    disabledLength > 0 && !ipInterface.ip
                      ? ipInterface.subnet
                          .split('.')
                          .slice(0, disabledLength)
                          .join('.')
                      : ipInterface.ip;

                  return (
                    <Fragment key={index}>
                      <tr>
                        <td className="border border-gray-300 text-center">
                          {ipInterface.vlanId}
                        </td>
                        <td className="border border-gray-300">
                          <IPInput
                            id={`node-ip-${index}`}
                            name={`nodeIp-${index}`}
                            value={inputValue}
                            placeholder="192.168.1.0"
                            wrapperClassName="w-auto"
                            className="rounded-none border-none"
                            disabledLength={disabledLength}
                            error={errors.ipInterfaces[index]?.ip}
                            onChange={(value) => {
                              let newValue = '';
                              const valueParts = value
                                .split('.')
                                .filter((part) => part !== '');
                              if (valueParts.length > disabledLength) {
                                newValue = value;
                              }
                              setValues((prev) => ({
                                ...prev,
                                ipInterfaces: prev.ipInterfaces.map((d, i) =>
                                  i === index ? { ...d, ip: newValue } : d,
                                ),
                              }));
                            }}
                            dataTestId={`node-edit-ip-input-vlan-${id}-${ipInterface.vlanId}`}
                          />
                        </td>
                        <td className="border border-gray-300">
                          <IPInput
                            id={`node-subnet-mask-${index}`}
                            name={`nodeSubNetmask-${index}`}
                            className="rounded-none border-none"
                            placeholder="255.255.255.0"
                            disabled={!!ipInterface.subnetMask}
                            value={ipInterface.subnetMask}
                            error={errors.ipInterfaces[index]?.subnetMask}
                            onChange={(value) => {
                              setValues((prev) => ({
                                ...prev,
                                ipInterfaces: prev.ipInterfaces.map((d, i) =>
                                  i === index ? { ...d, subnetMask: value } : d,
                                ),
                              }));
                            }}
                            dataTestId={`node-edit-subnet-mask-input-vlan-${id}-${ipInterface.vlanId}`}
                          />
                        </td>
                        {type === EDataType.Device && (
                          <td className="border border-gray-300">
                            <div className="flex w-full items-center justify-center">
                              <FormSwitch
                                id={`node-dhcp-${index}`}
                                name={`nodeDhcp-${index}`}
                                wrapperClassName="flex-col w-auto items-start justify-start gap-1"
                                defaultChecked={ipInterface.dhcp || false}
                                onChange={(_value) => {
                                  setValues((prev) => ({
                                    ...prev,
                                    dhcp: _value,
                                    ipInterfaces: prev.ipInterfaces.map(
                                      (d, i) =>
                                        i === index
                                          ? { ...d, dhcp: _value }
                                          : d,
                                    ),
                                  }));
                                }}
                                data-testid={`node-edit-dhcp-switch-vlan-${id}-${ipInterface.vlanId}`}
                              />
                            </div>
                          </td>
                        )}
                      </tr>
                      {errors.ipInterfaces[index]?.subnet &&
                        !errors.ipInterfaces[index]?.ip &&
                        !errors.ipInterfaces[index]?.subnetMask && (
                          <tr>
                            <td colSpan={4}>
                              <p className="text-left text-sm text-red-500">
                                {errors.ipInterfaces[index]?.subnet}
                              </p>
                            </td>
                          </tr>
                        )}
                    </Fragment>
                  );
                })}
              </tbody>
            ) : (
              <tbody>
                <EditIpNoVlan
                  id={id}
                  type={type}
                  globalIpSubnet={globalIpSubnet || undefined}
                  values={values}
                  setValues={setValues}
                  errors={errors}
                />
                {errors.subnet && !errors.ip && !errors.subnetMask && (
                  <tr>
                    <td colSpan={4}>
                      <p className="text-left text-sm text-red-500">
                        {errors.subnet}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </table>
          {type === EDataType.Switch && (
            <div className="mt-3 flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700">Gateway</span>
              <IPInput
                id={`node-gateway-${id}`}
                name="nodeGateway"
                value={values.gateway || ''}
                placeholder="192.168.1.1"
                wrapperClassName="w-auto"
                error={errors.gateway}
                onChange={(value) =>
                  setValues((prev) => ({ ...prev, gateway: value }))
                }
                dataTestId={`node-edit-gateway-input-${id}`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const EditIpNoVlan = ({
  id,
  globalIpSubnet,
  values,
  type,
  setValues,
  errors,
}: {
  id: string;
  type: EDataType;
  globalIpSubnet: GlobalIpSubnetType | undefined;
  values: INodeEditData;
  setValues: (value: React.SetStateAction<INodeEditData>) => void;
  errors: {
    ip: string;
    subnetMask: string;
  };
}) => {
  const disabledLength = globalIpSubnet?.subnetMask
    ? countLeading255Octets(globalIpSubnet.subnetMask)
    : -1;

  const inputValue =
    disabledLength > 0 && !values.ip
      ? globalIpSubnet?.ipSubnet?.split('.').slice(0, disabledLength).join('.')
      : values.ip || '';

  const hasGlobalIpSubnet = globalIpSubnet?.subnetMask;

  return (
    <tr>
      <td className="border border-gray-300">
        <IPInput
          id="node-ip"
          name="nodeIp"
          value={inputValue ?? ''}
          placeholder="192.168.1.0"
          wrapperClassName="w-auto"
          className="rounded-none border-none"
          disabledLength={disabledLength}
          error={errors.ip}
          onChange={(value) => {
            let newValue = '';

            const valueParts = value.split('.').filter((part) => part !== '');
            if (valueParts.length > disabledLength) {
              newValue = value;
            }

            setValues((prev) => ({
              ...prev,
              ip: newValue,
              ...(hasGlobalIpSubnet
                ? { subnetMask: newValue ? globalIpSubnet.subnetMask : '' }
                : {}),
            }));
          }}
          dataTestId={`node-edit-ip-input-no-vlan-${id}`}
        />
      </td>
      <td className="border border-gray-300">
        <IPInput
          id="node-subnet-mask"
          name="nodeSubNetmask"
          placeholder="255.255.255.0"
          className="rounded-none border-none"
          value={values.subnetMask || globalIpSubnet?.subnetMask || ''}
          error={errors.subnetMask}
          disabled={!!globalIpSubnet?.subnetMask}
          onChange={(value) => {
            setValues((prev) => ({
              ...prev,
              subnetMask: value,
            }));
          }}
          dataTestId={`node-edit-subnet-mask-input-no-vlan-${id}`}
        />
      </td>
      {type === EDataType.Device && (
        <td className="border border-gray-300">
          <div className="flex w-full items-center justify-center">
            <FormSwitch
              id="node-dhcp"
              name="nodeDhcp"
              wrapperClassName="flex-col w-auto items-start justify-start gap-1"
              checked={values.dhcp}
              onChange={(_value) => {
                setValues((prev) => ({ ...prev, dhcp: _value }));
              }}
              data-testid={`node-edit-dhcp-switch-no-vlan-${id}`}
            />
          </div>
        </td>
      )}
    </tr>
  );
};
